<template>
  <Modal
    ref="modal"
    v-model:open="open"
    :max-width="maxWidth"
    :min-width="minWidth"
    :max-height="maxHeight"
    :content-padding="!!Content"
    :title="options?.title"
    :btn-to-close="btnToClose"
  >
    <template v-if="Header" #header>
      <Header />
    </template>
    <Content v-if="Content" ref="content" class="p-2.5" />
    <div v-else class="flex flex-col gap-4 p-4">
      <div v-if="options?.message" class="text-body-default flex flex-col gap-2 break-words">
        <p v-for="p in messageAsParagraphs" :key="p">
          {{ p }}
        </p>
      </div>
      <div v-if="dialogType === 'prompt'" class="flex flex-col gap-2">
        <InputText
          v-model="promptValue"
          :placeholder="(options as IPromptDialogOptions)?.placeholder"
          :error="promptError"
        />
      </div>
    </div>
    <template v-if="Footer || !['modal', 'alert'].includes(dialogType)" #footer>
      <ModalFooter
        :confirm-text="confirmText"
        :destructive="(options as IPromptDialogOptions)?.destructive"
        :disabled="!canConfirm"
        @cancel="cancelClose"
        @confirm="confirmClose"
      >
        <Footer v-if="Footer" />
      </ModalFooter>
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import type {
    DialogType,
    IConfirmDialogOptions,
    IDialogOptions,
    IModalOptions,
    IPromptDialogOptions,
  } from "~/types/dialog";

  const dialogType = ref<DialogType | "">("");
  const content = ref(null);

  const options = ref<IDialogOptions | IPromptDialogOptions | IConfirmDialogOptions | IModalOptions | null>();

  const { t } = useI18n();

  const modal = ref<HTMLDivElement | null | undefined>();
  const promptValue = ref("");
  const promptError = ref("");

  const Content = computed(() => (options.value as IDialogOptions)?.content);
  const Footer = computed(() => (options.value as IDialogOptions)?.footer);
  const Header = computed(() => (options.value as IDialogOptions)?.header);
  const width = computed(() => (options.value as IModalOptions)?.width);
  const btnToClose = computed(() => (options.value as IModalOptions)?.btnToClose);

  const minWidth = computed(() => (options.value as IModalOptions)?.minWidth || width.value || "342px");
  const maxWidth = computed(() => (options.value as IModalOptions)?.maxWidth || width.value || "342px");
  const maxHeight = computed(() => (options.value as IModalOptions)?.maxHeight);
  const open = computed({
    get: () => dialogType.value !== "",
    set: (value: boolean) => {
      if (!value) {
        dialogType.value = "";
        options.value = null;
      }
    },
  });

  const canConfirm = computed(() => {
    if (content.value?.canConfirm !== undefined) {
      return content.value.canConfirm;
    }

    return true;
  });

  watch(
    () => promptValue.value,
    () => {
      promptError.value = "";
    }
  );

  const confirmClose = () => {
    if (dialogType.value === "prompt") {
      const promptOptions = options.value as IPromptDialogOptions;
      const prompt = promptOptions?.prompt;

      if (prompt && promptValue.value !== prompt) {
        promptError.value = t("default_prompt_error");
        return;
      }

      promptOptions?.onConfirm?.(promptValue.value || "");
    } else {
      (options.value as IConfirmDialogOptions)?.onConfirm?.();
    }
    open.value = false;
  };

  const cancelClose = () => {
    (options.value as IConfirmDialogOptions)?.onCancel?.();
    open.value = false;
  };

  const confirmText = computed(() => {
    return (options.value as IConfirmDialogOptions)?.confirmText || t("ok");
  });

  const cancelText = computed(() => {
    return (options.value as IConfirmDialogOptions)?.cancelText || t("undo");
  });

  const messageAsParagraphs = computed(() => {
    if (!options.value?.message) return [];
    return options.value.message.split("\n");
  });

  onMounted(() => {
    EventBus.$on(
      "DIALOG_OPEN",
      (type: DialogType, opts: IDialogOptions | IPromptDialogOptions | IConfirmDialogOptions) => {
        dialogType.value = type;
        options.value = opts;
      }
    );

    EventBus.$on("DIALOG_CLOSE", () => {
      open.value = false;
    });
  });

  onUnmounted(() => {
    EventBus.$off("DIALOG_OPEN");
    EventBus.$off("DIALOG_CLOSE");
  });
</script>
